import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Brain, X, Plus, Calendar, FileText, AlertCircle } from "lucide-react";
import { useDesignSystem, cn } from '../../designSystem';

const MobileNavigation = ({ 
  isMobileMenuOpen, 
  setIsMobileMenuOpen,
  konsultasjoner,
  currentKonsultasjon,
  setCurrentKonsultasjon,
  startNewConsultation,
  setTranscription,
  setNote,
  setChosenTemplate,
  DEFAULT_TEMPLATE,
  isLandscape
}) => {
  const { colors, typography, componentStyles, effects, shadows, isDarkMode, setIsDarkMode } = useDesignSystem();
  
  return (
    <div className={cn(
      "fixed inset-0",
      colors.neutral.bg,
      "z-50 transform",
      effects.transitionSlow,
      isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
    )}>
      <div className="flex flex-col h-full">
        <div className={cn(
          "p-4", 
          colors.neutral.border, 
          "border-b",
          isLandscape && "p-2"
        )}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={cn(
                colors.primary.gradient, 
                isLandscape ? "p-2 rounded-lg" : "p-2.5 rounded-xl"
              )}>
                <Brain className={isLandscape ? "h-4 w-4 text-white" : "h-5 w-5 text-white"} />
              </div>
              <h2 className={cn(
                typography.gradient, 
                isLandscape ? "text-lg" : "text-xl font-semibold"
              )}>
                Skribo
              </h2>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(false)}
              className={cn(
                componentStyles.button.base,
                componentStyles.button.ghost,
                colors.neutral.textSecondary,
                isLandscape && "h-8 w-8"
              )}
            >
              <X className={isLandscape ? "h-5 w-5" : "h-6 w-6"} />
            </Button>
          </div>
        </div>
        
        <div className={cn(
          "p-4", 
          colors.neutral.border, 
          "border-b",
          isLandscape && "p-2"
        )}>
          <Button
            onClick={() => {
              startNewConsultation();
              setIsMobileMenuOpen(false);
            }}
            className={cn(
              "w-full",
              componentStyles.button.base,
              componentStyles.button.primary,
              isLandscape && "h-8 text-xs py-0"
            )}
          >
            <Plus className="mr-2 h-4 w-4" /> Ny Konsultasjon
          </Button>
        </div>
        
        <ScrollArea className={cn(
          "flex-1 px-4 py-2",
          isLandscape && "py-0"
        )}>
          <div className="space-y-3">
            <h3 className={cn(
              typography.small,
              typography.medium,
              colors.neutral.textTertiary,
              "px-1 py-2",
              isLandscape && "py-1"
            )}>
              Dine konsultasjoner
            </h3>
            
            {konsultasjoner.length === 0 ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-50 dark:bg-neutral-800 mb-3">
                  <FileText className="h-6 w-6 text-blue-500 dark:text-blue-400" />
                </div>
                <p className={cn(typography.small, colors.neutral.textTertiary)}>
                  Ingen konsultasjoner ennå
                </p>
              </div>
            ) : (
              <div className={cn(
                "space-y-2",
                isLandscape && "grid grid-cols-2 gap-2 space-y-0"
              )}>
                {konsultasjoner.map((k) => (
                  <Card
                    key={k.id}
                    className={cn(
                      "cursor-pointer",
                      effects.transition,
                      effects.hoverSlideRight,
                      currentKonsultasjon?.id === k.id 
                        ? componentStyles.card.active
                        : cn(colors.neutral.border, "dark:bg-neutral-800/50")
                    )}
                    onClick={() => {
                      setCurrentKonsultasjon(k);
                      setTranscription(k.transcript || '');
                      setNote(k.notat || '');
                      setChosenTemplate(k.chosen_mal || DEFAULT_TEMPLATE);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <CardContent className={cn(
                      "p-3",
                      isLandscape && "p-2"
                    )}>
                      <div className="flex justify-between items-start">
                        <div>
                          <div className={cn(
                            typography.cardTitle, 
                            "truncate",
                            isLandscape && "text-sm"
                          )}>
                            {k.title || "Uten tittel"}
                          </div>
                          <div className={cn(
                            "flex items-center",
                            typography.small,
                            colors.neutral.textSecondary,
                            "space-x-2 mt-1",
                            isLandscape && "text-xs mt-0.5"
                          )}>
                            <Calendar className="h-3 w-3" />
                            <span>{new Date(k.start_time).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className={cn(
                          componentStyles.badge.base,
                          k.status === "ongoing" 
                            ? componentStyles.badge.primary
                            : componentStyles.badge.success,
                          isLandscape && "text-[10px] px-1.5 py-0"
                        )}>
                          {k.status === "ongoing" ? "Pågår" : "Fullført"}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
        
        <div className={cn(
          "p-4 border-t dark:border-neutral-800",
          isLandscape && "p-2"
        )}>
        <div className={cn(
          "p-2 border rounded-md bg-amber-50 dark:bg-neutral-800 border-amber-200 dark:border-neutral-700",
          typography.small,
          typography.center,
          isLandscape && "p-1 text-xs"
        )}>
            <div className={cn(
              "flex items-center justify-center gap-2 mb-1",
              isLandscape && "mb-0.5 gap-1"
            )}>
              <AlertCircle className={cn("h-4 w-4 text-amber-600 dark:text-amber-500", isLandscape && "h-3 w-3")} />
              <span className="font-medium text-amber-600 dark:text-amber-500">
                Automatisk sletting
              </span>
            </div>
            <p>
              Alle konsultasjoner slettes automatisk etter 24 timer.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileNavigation;